import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getStudent, getCourse } from '../../api/instructorApi';
import { toTitleCase } from '../../utils/format';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function InstructorStudentDetail() {
  const { courseId, studentId } = useParams();
  const navigate = useNavigate();

  const [course,      setCourse]      = useState(null);
  const [student,     setStudent]     = useState(null);
  const [enrollment,  setEnrollment]  = useState(null);
  const [quizScores,  setQuizScores]  = useState([]);
  const [submissions, setSubmissions] = useState([]);

  useEffect(() => {
    getCourse(Number(courseId)).then(d => setCourse(d.course)).catch(console.error);
    getStudent(Number(courseId), Number(studentId)).then(d => {
      setStudent(d.student);
      setEnrollment(d.enrollment);
      setQuizScores(d.quiz_scores);
      setSubmissions(d.submissions);
    }).catch(console.error);
  }, [courseId, studentId]);

  if (!student || !course) return <div className="body" style={{ color: 'var(--muted)' }}>Loading…</div>;

  const status = enrollment?.progress_percent === 100 ? 'Complete'
    : enrollment?.progress_percent > 0 ? 'In Progress' : 'Not Started';
  const sCls = status === 'Complete' ? 'b-g' : status === 'In Progress' ? 'b-o' : 'b-gr';

  return (
    <>
      {/* ── Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Instructor / Students / {toTitleCase(student.full_name)}</div>
          <div className="pt">{toTitleCase(student.full_name)}</div>
          <div className="ps">{student.email} · {course.title}</div>
        </div>
        <button className="btn btn-o btn-sm" onClick={() => navigate(`/instructor/courses/${courseId}/students`)}>
          ← Students
        </button>
      </div>

      <div className="body">
        {/* ── Progress Summary ── */}
        <div className="stats" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
          <div className="sc">
            <div className="sc-l">Progress</div>
            <div className="sc-v">{enrollment?.progress_percent ?? 0}%</div>
            <div className="sc-bar">
              <div className="sc-fill" style={{ width: `${enrollment?.progress_percent ?? 0}%`, background: 'var(--yellow)' }} />
            </div>
          </div>
          <div className="sc">
            <div className="sc-l">Modules Completed</div>
            <div className="sc-v">{enrollment?.modules_completed ?? 0}/{enrollment?.modules_total ?? 0}</div>
          </div>
          <div className="sc">
            <div className="sc-l">Status</div>
            <div className="sc-v" style={{ fontSize: 16 }}>
              <span className={`b ${sCls}`}>{status}</span>
            </div>
          </div>
        </div>

        {/* ── Quiz Scores ── */}
        <div className="card">
          <div className="ct">Quiz Scores by Module</div>
          {quizScores.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '12px 0' }}>No modules yet</div>
          ) : (
            quizScores.map((qs, i) => (
              <div
                key={qs.module_id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '11px 0',
                  borderBottom: i < quizScores.length - 1 ? '1px solid var(--border)' : 'none',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 500 }}>
                  Module {i + 1}: {qs.module_title}
                </span>
                <span className={`b ${qs.score === null ? 'b-gr' : qs.score >= 70 ? 'b-g' : 'b-r'}`}>
                  {qs.score === null ? 'Not taken' : `${qs.score}%`}
                </span>
              </div>
            ))
          )}
        </div>

        {/* ── Assignment Submissions ── */}
        <div className="card">
          <div className="ct">Assignment Submissions</div>
          {submissions.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '12px 0' }}>No assignments yet</div>
          ) : (
            submissions.map((sub, i) => (
              <div
                key={sub.assignment_id}
                style={{
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                  padding: '14px 0',
                  borderBottom: i < submissions.length - 1 ? '1px solid var(--border)' : 'none',
                  gap: 12,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 3 }}>{sub.assignment_title}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                    Due {fmtDate(sub.due_date)}
                    {sub.submitted && sub.submitted_at && ` · Submitted ${fmtDate(sub.submitted_at)}`}
                  </div>
                  {sub.submitted && sub.file_name && (
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>📎 {sub.file_name}</div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span className={`b ${sub.submitted ? 'b-g' : 'b-gr'}`}>
                    {sub.submitted ? 'Submitted' : 'Not Submitted'}
                  </span>
                  {sub.submitted && (
                    <span className={`b ${sub.grade ? 'b-g' : 'b-o'}`}>
                      {sub.grade ? sub.grade : 'Pending'}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
